<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex items-center justify-between gap-4">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">
          Store Team
        </h1>
        <p class="mt-1 text-gray-600 dark:text-gray-400">
          Manage users and permissions for your store
        </p>
      </div>
      <button
        @click="showInviteModal = true"
        class="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium transition"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        Invite User
      </button>
    </div>

    <!-- Tabs -->
    <div class="flex gap-4 border-b border-gray-200 dark:border-gray-700">
      <button
        @click="activeTab = 'invitations'"
        :class="[
          'px-4 py-3 font-medium border-b-2 transition',
          activeTab === 'invitations'
            ? 'border-blue-500 text-blue-600 dark:text-blue-400'
            : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300'
        ]"
      >
        Pending Invitations
        <span
          v-if="pendingInvitations.length > 0"
          class="ml-2 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs font-semibold"
        >
          {{ pendingInvitations.length }}
        </span>
      </button>
      <button
        @click="activeTab = 'users'"
        :class="[
          'px-4 py-3 font-medium border-b-2 transition',
          activeTab === 'users'
            ? 'border-blue-500 text-blue-600 dark:text-blue-400'
            : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300'
        ]"
      >
        Active Users
        <span
          v-if="storeUsers.length > 0"
          class="ml-2 px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-xs font-semibold"
        >
          {{ storeUsers.length }}
        </span>
      </button>
    </div>

    <!-- Pending Invitations Tab -->
    <div v-if="activeTab === 'invitations'">
      <PendingInvitationsList
        :store-id="currentStore?.id"
        :invitations="pendingInvitations"
        :is-loading="isLoadingInvitations"
        @revoke="revokeInvitation"
        @refresh="loadInvitations"
      />
    </div>

    <!-- Active Users Tab -->
    <div v-if="activeTab === 'users'">
      <StoreUsersList
        :store-id="currentStore?.id"
        :users="storeUsers"
        :is-loading="isLoadingUsers"
        @update-permissions="updateUserPermissions"
        @remove-user="removeUser"
      />
    </div>

    <!-- Invite Modal -->
    <InviteUserModal
      :is-open="showInviteModal"
      :store-id="currentStore?.id"
      :available-permissions="availablePermissions"
      @close="showInviteModal = false"
      @invite-sent="handleInviteSent"
    />

    <!-- Success Toast -->
    <div
      v-if="successMessage"
      class="fixed bottom-4 right-4 px-4 py-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 rounded-lg shadow-lg"
    >
      {{ successMessage }}
    </div>
    <div
      v-if="errorMessage"
      class="fixed bottom-4 right-4 px-4 py-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded-lg shadow-lg"
    >
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import InviteUserModal from '@/components/StoreUsers/InviteUserModal.vue';
import PendingInvitationsList from '@/components/StoreUsers/PendingInvitationsList.vue';
import StoreUsersList from '@/components/StoreUsers/StoreUsersList.vue';
import storeUsersService from '@/services/storeUsersService';

export default {
  name: 'UsersView',
  components: {
    InviteUserModal,
    PendingInvitationsList,
    StoreUsersList,
  },
  setup() {
    const store = useStore();
    const currentStore = computed(() => store.state.store?.selectedStore);

    const activeTab = ref('invitations');
    const showInviteModal = ref(false);

    const pendingInvitations = ref([]);
    const storeUsers = ref([]);
    const isLoadingInvitations = ref(false);
    const isLoadingUsers = ref(false);

    const successMessage = ref('');
    const errorMessage = ref('');
    const availablePermissions = [
      'CAN_MANAGE_USERS',
      'CAN_MANAGE_INVENTORY',
      'CAN_MANAGE_REPORTS',
      'CAN_VIEW_REPORTS',
    ];

    const loadInvitations = async () => {
      if (!currentStore.value?.id) return;

      isLoadingInvitations.value = true;
      try {
        const response = await storeUsersService.getPendingInvitations(
          currentStore.value.id
        );
        pendingInvitations.value = response.data?.data || [];
      } catch (error) {
        console.error('Failed to load invitations:', error);
      } finally {
        isLoadingInvitations.value = false;
      }
    };

    const loadUsers = async () => {
      if (!currentStore.value?.id) return;

      isLoadingUsers.value = true;
      try {
        const response = await storeUsersService.getStoreUsers(
          currentStore.value.id
        );
        storeUsers.value = response.data?.data || [];
      } catch (error) {
        console.error('Failed to load users:', error);
      } finally {
        isLoadingUsers.value = false;
      }
    };

    const handleInviteSent = async (data) => {
      errorMessage.value = '';
      try {
        const response = await storeUsersService.inviteUser(data.payload);
        const recipient =
          data.method === 'email' ? data.payload.email : data.payload.phone_number;
        if (!response.data?.data?.invitation) {
          successMessage.value = `Store access updated for ${recipient}`;
          setTimeout(() => (successMessage.value = ''), 3000);
          await loadUsers();
          return;
        }
        const notificationSent =
          data.method === 'email'
            ? response.data?.data?.email_sent
            : response.data?.data?.sms_sent;
        if (notificationSent) {
          successMessage.value = `Invitation sent to ${recipient}`;
          setTimeout(() => (successMessage.value = ''), 3000);
        } else {
          errorMessage.value =
            `Invitation created for ${recipient}, but notification delivery failed`;
          setTimeout(() => (errorMessage.value = ''), 5000);
        }
        await loadInvitations();
      } catch (error) {
        console.error('Failed to invite user:', error);
        const retryAfter =
          error.response?.data?.error?.retry_after_seconds ||
          error.response?.data?.data?.retry_after_seconds;
        errorMessage.value =
          retryAfter
            ? `Too many invitation attempts. Try again in ${retryAfter} seconds.`
            : error.response?.data?.error?.message || 'Failed to create invitation';
        setTimeout(() => (errorMessage.value = ''), 5000);
      }
    };

    const revokeInvitation = async (invitationId) => {
      try {
        await storeUsersService.revokeInvitation(invitationId);
        successMessage.value = 'Invitation revoked';
        setTimeout(() => (successMessage.value = ''), 3000);
        await loadInvitations();
      } catch (error) {
        console.error('Failed to revoke invitation:', error);
      }
    };

    const updateUserPermissions = async (userId, permissions) => {
      try {
        await storeUsersService.updateUserPermissions({
          user_store_permission_id: userId,
          permissions,
        });
        successMessage.value = 'Permissions updated';
        setTimeout(() => (successMessage.value = ''), 3000);
        await loadUsers();
      } catch (error) {
        console.error('Failed to update permissions:', error);
      }
    };

    const removeUser = async (permissionId) => {
      try {
        await storeUsersService.removeUserFromStore(permissionId);
        successMessage.value = 'User removed from store';
        setTimeout(() => (successMessage.value = ''), 3000);
        await loadUsers();
      } catch (error) {
        console.error('Failed to remove user:', error);
      }
    };

    onMounted(() => {
      if (currentStore.value?.id) {
        loadInvitations();
        loadUsers();
      }
    });

    return {
      currentStore,
      activeTab,
      showInviteModal,
      pendingInvitations,
      storeUsers,
      isLoadingInvitations,
      isLoadingUsers,
      successMessage,
      errorMessage,
      availablePermissions,
      loadInvitations,
      handleInviteSent,
      revokeInvitation,
      updateUserPermissions,
      removeUser,
    };
  },
};
</script>
  
  